/**
 * PlatformPartners Page
 *
 * Management Console page for superadmins to manage partners
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  AlertTriangle,
  Users,
  RotateCcw,
  Crown,
  Link as LinkIcon,
  ExternalLink,
  ArrowLeft,
  PlayCircle,
  PauseCircle
} from 'lucide-react';
import { PartnerModal, PartnerFormData } from '@/components/management/PartnerModal';
import { apiService } from '@/services/api';
import { toast } from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, ColumnDefinition, ActionDefinition, PaginationState, SortState } from '@/components/shared/DataTable';
import { FilterBar, FilterConfig } from '@/components/shared/FilterBar';
import { useFilterParams } from '@/hooks/useFilterParams';
import { BulkActionsBar, BulkAction } from '@/components/shared/BulkActionsBar';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { Button } from '@/components/ui/button';
import { DeactivateConfirmModal, CascadeImpact } from '@/components/deletion';
import { useAuth } from '@/hooks/useAuth';

interface Partner {
  id: string;
  name: string;
  slug: string;
  type: 'reseller' | 'affiliate' | 'enterprise' | 'internal';
  status: 'active' | 'suspended' | 'pending' | 'cancelled';
  isOwnerPartner: boolean;
  enterpriseId?: string;
  enterpriseName?: string;
  contact: {
    primaryName: string;
    primaryEmail: string;
    primaryPhone?: string;
  };
  quotas: {
    maxOrganizations: number;
    maxUsersPerOrg: number;
    maxStorageGB: number;
  };
  stats: {
    organizationCount: number;
    userCount: number;
    quotaUsage: {
      organizations: number;
      maxOrganizations: number;
    };
  };
  createdAt: string;
  isDeleted?: boolean;
}

interface Enterprise {
  id: string;
  name: string;
  isDeleted?: boolean;
}

// Filter configuration for FilterBar
const filterConfig: FilterConfig = {
  filters: [
    {
      key: 'search',
      type: 'search',
      label: 'Search',
      placeholder: 'Search partners...',
      width: 'lg',
      debounceMs: 300,
    },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      placeholder: 'All Statuses',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'suspended', label: 'Suspended' },
        { value: 'pending', label: 'Pending' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
    },
    {
      key: 'type',
      type: 'select',
      label: 'Type',
      placeholder: 'All Types',
      options: [
        { value: 'reseller', label: 'Reseller' },
        { value: 'affiliate', label: 'Affiliate' },
        { value: 'enterprise', label: 'Enterprise' },
        { value: 'internal', label: 'Internal' },
      ],
    },
  ],
  showActiveChips: true,
  showClearAll: true,
};

const PlatformPartners: React.FC = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);

  // Deactivate state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatingPartner, setDeactivatingPartner] = useState<Partner | null>(null);
  const [cascadeImpact, setCascadeImpact] = useState<CascadeImpact | undefined>(undefined);
  const [isDeactivating, setIsDeactivating] = useState(false);

  // Hard delete state
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [hardDeletingPartner, setHardDeletingPartner] = useState<Partner | null>(null);
  const [hardDeleteConfirmName, setHardDeleteConfirmName] = useState('');
  const [hardDeleteCheckbox, setHardDeleteCheckbox] = useState(false);
  const [isHardDeleting, setIsHardDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state with URL sync
  const { filters, setFilter, clearFilter, clearAllFilters, activeFilters } = useFilterParams<{
    search: string;
    status: string;
    type: string;
    orgStatus: string;
  }>({
    schema: {
      search: { type: 'string', default: '', debounce: 300 },
      status: { type: 'string', default: '' },
      type: { type: 'string', default: '' },
      orgStatus: { type: 'string', default: 'active' },
    },
    labels: {
      search: 'Search',
      status: 'Status',
      type: 'Type',
      orgStatus: 'Status',
    },
  });

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 20,
    total: 0,
  });

  // Sorting state
  const [sortState, setSortState] = useState<SortState>({
    column: 'name',
    direction: 'asc',
  });

  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());


  // Get enterpriseId filter from URL params
  const enterpriseIdFilter = searchParams.get('enterpriseId');

  // Find enterprise name for display
  const filteringEnterprise = useMemo(() => {
    if (!enterpriseIdFilter) return null;
    return enterprises.find(e => e.id === enterpriseIdFilter);
  }, [enterpriseIdFilter, enterprises]);

  // Clear enterprise filter
  const clearEnterpriseFilter = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('enterpriseId');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);


  // Assign organizations modal state
  useEffect(() => {
    loadPartners();
    loadEnterprises();
  }, [filters.status, filters.type, enterpriseIdFilter]);

  const loadEnterprises = async () => {
    try {
      interface EnterprisesResponse {
        success: boolean;
        data: {
          enterprises: Enterprise[];
          totalCount: number;
        }
      }
      const response = await apiService.get<EnterprisesResponse>('/v1/platform/enterprises');
      setEnterprises((response.data.enterprises || []).filter(e => !e.isDeleted));
    } catch (error) {
      console.error('Failed to load enterprises:', error);
    }
  };

  const loadPartners = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('includeDeleted', 'true');
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (enterpriseIdFilter) params.append('enterpriseId', enterpriseIdFilter);

      interface PartnersResponse { success: boolean; data: Partner[] }
      const response = await apiService.get<PartnersResponse>(`/v1/platform/partners?${params.toString()}`);
      setPartners(response.data || []);
    } catch (error) {
      console.error('Failed to load partners:', error);
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingPartner(null);
    setShowCreateModal(true);
  };

  const handleEditPartner = useCallback((partner: Partner) => {
    setEditingPartner(partner);
    setShowCreateModal(true);
  }, []);

  const handleSavePartner = async (data: PartnerFormData) => {
    if (editingPartner) {
      await apiService.put(`/v1/platform/partners/${editingPartner.id}`, data);
      toast.success('Partner updated successfully');
    } else {
      await apiService.post('/v1/platform/partners', data);
      toast.success('Partner created successfully');
    }
    setEditingPartner(null);
    loadPartners();
  };

  // Initiate deactivation - gets cascade impact for the confirmation modal
  const initiateDeactivate = useCallback(async (partner: Partner) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/v1/platform/partners/${partner.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to get deactivation info');
      }

      if (result.requiresConfirmation) {
        setDeactivatingPartner(partner);
        setCascadeImpact(result.cascadeImpact);
        setShowDeactivateModal(true);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to initiate deactivation');
    }
  }, []);

  // Execute deactivation with name confirmation
  const handleDeactivatePartner = useCallback(async (confirmName: string) => {
    if (!deactivatingPartner) return;

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      setIsDeactivating(true);
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/v1/platform/partners/${deactivatingPartner.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirmName })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to deactivate partner');
      }

      setShowDeactivateModal(false);
      setDeactivatingPartner(null);
      setCascadeImpact(undefined);
      toast.success('Partner deactivated successfully');
      loadPartners();
    } catch (error: any) {
      toast.error(error.message || 'Failed to deactivate partner');
    } finally {
      setIsDeactivating(false);
    }
  }, [deactivatingPartner, loadPartners]);

  const handleReactivatePartner = useCallback(async (partnerId: string) => {
    try {
      await apiService.post(`/v1/platform/partners/${partnerId}/reactivate`);
      toast.success('Partner reactivated successfully');
      loadPartners();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to reactivate partner');
    }
  }, [loadPartners]);

  const handleHardDelete = async () => {
    if (!hardDeletingPartner?.id) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    setIsHardDeleting(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/api/v1/platform/partners/${hardDeletingPartner.id}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmName: hardDeleteConfirmName, confirmCheckbox: hardDeleteCheckbox })
      });
      const result = await response.json();
      if (result.success) {
        setShowHardDeleteModal(false);
        setHardDeletingPartner(null);
        loadPartners();
      } else {
        setError(result.error || 'Failed to permanently delete');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to permanently delete partner');
    } finally {
      setIsHardDeleting(false);
    }
  };

  // Bulk action handlers
  const handleBulkActivate = useCallback(async (ids: string[]) => {
    try {
      await Promise.all(ids.map(id => apiService.put(`/v1/platform/partners/${id}`, { status: 'active' })));
      toast.success(`${ids.length} partner${ids.length > 1 ? 's' : ''} activated`);
      setSelectedIds(new Set());
      loadPartners();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to activate partners');
    }
  }, [loadPartners]);

  const handleBulkSuspend = useCallback(async (ids: string[]) => {
    try {
      await Promise.all(ids.map(id => apiService.put(`/v1/platform/partners/${id}`, { status: 'suspended' })));
      toast.success(`${ids.length} partner${ids.length > 1 ? 's' : ''} suspended`);
      setSelectedIds(new Set());
      loadPartners();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to suspend partners');
    }
  }, [loadPartners]);

  const handleBulkDelete = useCallback(async (ids: string[]) => {
    try {
      await Promise.all(ids.map(id => apiService.delete(`/v1/platform/partners/${id}`)));
      toast.success(`${ids.length} partner${ids.length > 1 ? 's' : ''} deleted`);
      setSelectedIds(new Set());
      loadPartners();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete partners');
    }
  }, [loadPartners]);

  // Bulk actions configuration
  const bulkActions: BulkAction<Partner>[] = useMemo(() => [
    {
      id: 'activate',
      label: 'Activate',
      icon: <PlayCircle className="h-4 w-4" />,
      variant: 'success',
      onClick: handleBulkActivate,
      show: (selectedItems) => selectedItems.some(p => p.status !== 'active' && !p.isDeleted && !p.isOwnerPartner),
    },
    {
      id: 'suspend',
      label: 'Suspend',
      icon: <PauseCircle className="h-4 w-4" />,
      variant: 'warning',
      onClick: handleBulkSuspend,
      show: (selectedItems) => selectedItems.some(p => p.status === 'active' && !p.isDeleted && !p.isOwnerPartner),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'danger',
      onClick: handleBulkDelete,
      confirm: {
        title: 'Delete Partners',
        message: 'Are you sure you want to delete the selected partners? This will also delete all organizations and deactivate all users.',
        confirmText: 'Delete',
      },
      show: (selectedItems) => selectedItems.some(p => !p.isDeleted && !p.isOwnerPartner),
    },
  ], [handleBulkActivate, handleBulkSuspend, handleBulkDelete]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'suspended':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'reseller': return 'bg-blue-100 text-blue-800';
      case 'affiliate': return 'bg-green-100 text-green-800';
      case 'enterprise': return 'bg-purple-100 text-purple-800';
      case 'internal': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Client-side search filtering
  const filteredPartners = useMemo(() => {
    let result = partners;

    // Status filter (active/deactivated tabs)
    const statusFilter = (filters.orgStatus as string) || 'active';
    if (statusFilter === 'active') {
      result = result.filter(p => !p.isDeleted);
    } else if (statusFilter === 'deactivated') {
      result = result.filter(p => p.isDeleted);
    }

    const searchQuery = (filters.search || '').toLowerCase();
    if (searchQuery) {
      result = result.filter(partner =>
        partner.name.toLowerCase().includes(searchQuery) ||
        partner.slug.toLowerCase().includes(searchQuery) ||
        partner.contact.primaryEmail.toLowerCase().includes(searchQuery)
      );
    }

    return result;
  }, [partners, filters.search, filters.orgStatus]);

  // Sorted data
  const sortedPartners = useMemo(() => {
    if (!sortState.column || !sortState.direction) return filteredPartners;

    return [...filteredPartners].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortState.column) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'type':
          aVal = a.type;
          bVal = b.type;
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'organizations':
          aVal = a.stats?.organizationCount || 0;
          bVal = b.stats?.organizationCount || 0;
          break;
        case 'users':
          aVal = a.stats?.userCount || 0;
          bVal = b.stats?.userCount || 0;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortState.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortState.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredPartners, sortState]);

  // Paginated data
  const paginatedPartners = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    const end = start + pagination.limit;
    return sortedPartners.slice(start, end);
  }, [sortedPartners, pagination.page, pagination.limit]);

  // Update pagination total when filtered data changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, total: filteredPartners.length, page: 1 }));
  }, [filteredPartners.length]);

  // DataTable column definitions with explicit widths for table-fixed layout
  const columns: ColumnDefinition<Partner>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Partner',
      sortable: true,
      sortKey: 'name',
      width: '25%',
      accessor: (partner) => (
        <div className="flex items-center gap-3 min-w-0">
          {partner.isOwnerPartner && (
            <span title="Owner Partner" className="flex-shrink-0">
              <Crown className="h-5 w-5 text-amber-500" />
            </span>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 truncate">{partner.name}</span>
              {partner.isDeleted && (
                <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700 flex-shrink-0">
                  Deleted
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 truncate">{partner.slug}</div>
          </div>
        </div>
      )
    },
    {
      id: 'type',
      header: 'Type',
      sortable: true,
      sortKey: 'type',
      width: '10%',
      hideOnTablet: true,
      accessor: (partner) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeBadgeColor(partner.type)}`}>
          {partner.type.toUpperCase()}
        </span>
      )
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortKey: 'status',
      width: '14%',
      accessor: (partner) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(partner.status)}
          <span className="text-sm text-gray-700 capitalize">{partner.status}</span>
        </div>
      )
    },
    {
      id: 'organizations',
      header: 'Orgs',
      sortable: true,
      sortKey: 'organizations',
      width: '13%',
      accessor: (partner) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/management/organizations?partnerId=${partner.id}`);
          }}
          className="text-sm text-purple-600 hover:text-purple-800 hover:underline"
        >
          {partner.stats?.organizationCount || 0}
          {partner.quotas.maxOrganizations !== -1 && (
            <span className="text-gray-400"> / {partner.quotas.maxOrganizations}</span>
          )}
        </button>
      )
    },
    {
      id: 'users',
      header: 'Users',
      sortable: true,
      sortKey: 'users',
      width: '10%',
      hideOnTablet: true,
      accessor: (partner) => (
        <span className="text-sm text-gray-700">{partner.stats?.userCount || 0}</span>
      )
    },
    {
      id: 'createdAt',
      header: 'Created',
      sortable: true,
      sortKey: 'createdAt',
      width: '12%',
      hideOnTablet: true,
      accessor: (partner) => (
        <span className="text-sm text-gray-500">
          {new Date(partner.createdAt).toLocaleDateString()}
        </span>
      )
    }
  ], [navigate]);

  // DataTable action definitions
  const actions: ActionDefinition<Partner>[] = useMemo(() => [
    {
      id: 'viewOrgs',
      icon: <ExternalLink className="h-4 w-4" />,
      tooltip: 'View organizations',
      onClick: (partner) => navigate(`/management/organizations?partnerId=${partner.id}`),
      variant: 'default'
    },
    {
      id: 'edit',
      icon: <Edit2 className="h-4 w-4" />,
      tooltip: 'Edit partner',
      onClick: (partner) => handleEditPartner(partner),
      variant: 'primary',
      show: (partner) => !partner.isOwnerPartner && !partner.isDeleted
    },
    {
      id: 'deactivate',
      icon: <Trash2 className="h-4 w-4" />,
      tooltip: 'Deactivate partner',
      onClick: (partner) => initiateDeactivate(partner),
      variant: 'danger',
      show: (partner) => !partner.isOwnerPartner && !partner.isDeleted
    },
    {
      id: 'reactivate',
      icon: <RotateCcw className="h-4 w-4" />,
      tooltip: 'Reactivate partner',
      onClick: (partner) => handleReactivatePartner(partner.id),
      variant: 'success',
      show: (partner) => partner.isDeleted === true
    },
    {
      id: 'permanentDelete',
      icon: <Trash2 className="h-4 w-4" />,
      tooltip: 'Permanently delete',
      onClick: (partner) => {
        setHardDeletingPartner(partner);
        setHardDeleteConfirmName('');
        setHardDeleteCheckbox(false);
        setShowHardDeleteModal(true);
      },
      show: (partner) => isSuperAdmin() && partner.isDeleted === true && !partner.isOwnerPartner,
      variant: 'danger',
    }
  ], [navigate, handleEditPartner, handleReactivatePartner, initiateDeactivate, isSuperAdmin]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enterprise Filter Banner */}
      {filteringEnterprise && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/management/enterprises')}
              className="p-1.5 hover:bg-purple-100 rounded-lg transition-colors"
              title="Back to Enterprises"
            >
              <ArrowLeft className="h-5 w-5 text-purple-600" />
            </button>
            <div>
              <span className="text-sm text-purple-700">
                Showing partners for enterprise:
              </span>
              <span className="ml-2 font-medium text-purple-900">
                {filteringEnterprise.name}
              </span>
            </div>
          </div>
          <button
            onClick={clearEnterpriseFilter}
            className="text-sm text-purple-600 hover:text-purple-800 hover:underline"
          >
            Show all partners
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Partners</h1>
          <p className="text-sm text-gray-500 mt-1">
            {filteringEnterprise
              ? `Partners under ${filteringEnterprise.name}`
              : 'Manage partner accounts and their organizations'
            }
          </p>
        </div>
        <button
          onClick={handleOpenCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Partner
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Partners</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {partners.filter(p => !p.isDeleted).length}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Partners</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {partners.filter(p => p.status === 'active' && !p.isDeleted).length}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Organizations</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {partners.reduce((sum, p) => sum + (p.stats?.organizationCount || 0), 0)}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {partners.reduce((sum, p) => sum + (p.stats?.userCount || 0), 0)}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <FilterBar
          config={filterConfig}
          values={filters}
          onChange={(key, value) => setFilter(key as keyof typeof filters, value as never)}
          onClear={(key) => clearFilter(key as keyof typeof filters)}
          onClearAll={clearAllFilters}
          activeFilters={activeFilters}
          loading={loading}
        />
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {[
          { value: 'active', label: 'Active', count: partners.filter(p => !p.isDeleted).length },
          { value: 'deactivated', label: 'Deactivated', count: partners.filter(p => p.isDeleted).length },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setFilter('orgStatus' as any, tab.value as never)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              (filters.orgStatus || 'active') === tab.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                (filters.orgStatus || 'active') === tab.value
                  ? tab.value === 'deactivated' ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Bulk Actions Bar */}
      <AnimatePresence>
        {selectedIds.size > 0 && (
          <BulkActionsBar
            selectedIds={selectedIds}
            items={filteredPartners}
            getItemId={(p) => p.id}
            actions={bulkActions}
            onClearSelection={() => setSelectedIds(new Set())}
            entityName="partner"
            theme="purple"
          />
        )}
      </AnimatePresence>

      {/* Partners Table - Using shared DataTable component */}
      <DataTable
        data={paginatedPartners}
        columns={columns}
        actions={actions}
        loading={loading}
        emptyMessage="No partners found"
        emptyIcon={<Users className="h-12 w-12 text-gray-300" />}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        sortable
        sortState={sortState}
        onSortChange={setSortState}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        onLimitChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
        rowClassName={(partner) => partner.isDeleted ? 'opacity-50' : ''}
        hoverable
        striped={false}
        ariaLabel="Partners table"
      />

      {/* Create/Edit Partner Modal */}
      <PartnerModal
        mode={editingPartner ? 'edit' : 'create'}
        partner={editingPartner}
        open={showCreateModal}
        onOpenChange={(open) => {
          setShowCreateModal(open);
          if (!open) setEditingPartner(null);
        }}
        onSave={handleSavePartner}
      />

      {/* Hard Delete Confirmation Modal */}
      {showHardDeleteModal && hardDeletingPartner && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setShowHardDeleteModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all">
              <div className="flex items-center justify-between px-6 py-4 border-b border-red-200 bg-red-50 rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-red-900">Permanently Delete Partner</h2>
                </div>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>This action is permanent and irreversible.</strong> All data including organizations,
                    users, patients, clinical encounters, recordings, and medical notes will be permanently deleted.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm text-gray-700">
                    To confirm, type{' '}
                    <span className="font-mono font-semibold bg-gray-100 px-1.5 py-0.5 rounded">{hardDeletingPartner.name}</span>
                    {' '}below:
                  </label>
                  <input
                    type="text"
                    value={hardDeleteConfirmName}
                    onChange={(e) => setHardDeleteConfirmName(e.target.value)}
                    placeholder="Type partner name to confirm"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    autoFocus
                    disabled={isHardDeleting}
                  />
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hardDeleteCheckbox}
                    onChange={(e) => setHardDeleteCheckbox(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    disabled={isHardDeleting}
                  />
                  <span className="text-sm text-gray-700">
                    I understand this will permanently delete all partner data and this action cannot be undone
                  </span>
                </label>
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button onClick={() => setShowHardDeleteModal(false)} disabled={isHardDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">Cancel</button>
                <button
                  onClick={handleHardDelete}
                  disabled={hardDeleteConfirmName !== hardDeletingPartner.name || !hardDeleteCheckbox || isHardDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isHardDeleting ? 'Deleting...' : 'I understand the consequences, delete this partner'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Modal */}
      <DeactivateConfirmModal
        isOpen={showDeactivateModal}
        entityType="channel_partner"
        entityName={deactivatingPartner?.name || ''}
        cascadeImpact={cascadeImpact}
        isLoading={isDeactivating}
        onConfirm={handleDeactivatePartner}
        onCancel={() => {
          setShowDeactivateModal(false);
          setDeactivatingPartner(null);
          setCascadeImpact(undefined);
        }}
      />
    </div>
  );
};

export default PlatformPartners;
